import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  RefreshCw, PlayCircle, ChevronDown, ChevronRight, CheckCircle2, XCircle, MinusCircle,
  Activity, AlertTriangle, Trash2, FlaskConical, Plug, Users, Search, ShieldAlert,
  ListChecks, UserPlus, UserMinus, Copy, Check, Clock, SearchCheck, Send, FileSpreadsheet,
} from 'lucide-react'
import dayjs from 'dayjs'
import { PageHeader } from '@/components/ui/PageHeader'
import { KpiCard } from '@/components/ui/KpiCard'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { Tabs } from '@/components/ui/Tabs'
import { Toggle } from '@/components/ui/Toggle'
import { EmptyState } from '@/components/ui/EmptyState'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Sheet } from '@/components/ui/Sheet'
import { DatePicker, type DatePickerMark } from '@/components/ui/DatePicker'
import { AsyncSelect, type AsyncOption } from '@/components/ui/AsyncSelect'
import { toast } from '@/components/ui/Toast'
import { dateTimeBR, maskCpf } from '@/lib/format'
import { extractApiError } from '@/lib/api-errors'
import { exportToXlsx, type ExportColumn } from '@/lib/exportXlsx'
import { useAuthority } from '@/hooks/useAuthority'
import {
  MedicarService,
  type MedicarAuthTest, type MedicarDryRun, type MedicarEligible, type MedicarOverview,
  type MedicarSyncRun, type MedicarSyncRunSummary, type MedicarSyncRequest, type MedicarRequestStatus,
  type MedicarPreview, type MedicarPreviewAction, type MedicarPreviewRow, type MedicarExchange,
  type MedicarPerson, type MedicarRunKind,
} from '@/services/modules/MedicarService'

const RUN_STATUS: Record<string, { label: string; variant: 'green' | 'amber' | 'red' | 'gray' }> = {
  CONCLUIDO:    { label: 'Concluída',    variant: 'green' },
  EM_ANDAMENTO: { label: 'Em andamento', variant: 'amber' },
  FALHOU:       { label: 'Falhou',       variant: 'red' },
}

const OPERATION: Record<string, { label: string; variant: 'blue' | 'violet' | 'amber' | 'gray' }> = {
  CREATE:  { label: 'Inclusão',              variant: 'blue' },
  EDIT:    { label: 'Correção de protocolo', variant: 'violet' },
  UPDATE:  { label: 'Alteração cadastral',   variant: 'violet' },
  BLOCK:   { label: 'Bloqueio',              variant: 'amber' },
  UNBLOCK: { label: 'Desbloqueio',           variant: 'blue' },
  LOOKUP:  { label: 'Consulta',              variant: 'gray' },
}

/**
 * As quatro situações em que uma vida pode cair na próxima rodada, mais o
 * cadastro incompleto — que não chega a ser enviado e por isso precisa
 * aparecer, senão a pessoa "some" sem explicação.
 */
const PREVIEW_ACTION: Record<MedicarPreviewAction, {
  label: string; short: string; variant: 'blue' | 'violet' | 'gray' | 'amber' | 'red'; icon: typeof UserPlus
}> = {
  CREATE:     { label: 'Primeira sincronização', short: 'Primeira',     variant: 'blue',   icon: UserPlus },
  AWAITING:   { label: 'Aguardando análise',     short: 'Em análise',   variant: 'gray',   icon: Clock },
  UPDATE:     { label: 'Alteração cadastral',    short: 'Alteração',    variant: 'violet', icon: RefreshCw },
  UNCHANGED:  { label: 'Sem alteração',          short: 'Sem alteração', variant: 'gray',  icon: MinusCircle },
  BLOCK:      { label: 'Bloqueio',               short: 'Bloqueio',     variant: 'amber',  icon: UserMinus },
  UNBLOCK:    { label: 'Desbloqueio',            short: 'Desbloqueio',  variant: 'blue',   icon: UserPlus },
  MANUAL:     { label: 'Exclusão manual',        short: 'Manual',       variant: 'amber',  icon: AlertTriangle },
  INCOMPLETE: { label: 'Cadastro incompleto',    short: 'Incompleto',   variant: 'red',    icon: AlertTriangle },
}

/** Como a rodada aparece no seletor e no cabeçalho do relatório. */
function kindLabel(kind: MedicarRunKind, userName: string | null): string {
  switch (kind) {
    case 'TEST':   return 'teste com um associado'
    case 'CHECK':  return 'consulta de protocolos'
    case 'RESEND': return 'reenvio de uma vida'
    default:       return userName ? `manual · ${userName}` : 'automática'
  }
}

/** Colunas do Excel da aba "Titulares elegíveis" — espelham a tabela. */
const ELIGIBLE_EXPORT_COLUMNS: ExportColumn<MedicarEligible>[] = [
  { header: 'Associado',           accessor: s => s.name },
  { header: 'CPF',                 accessor: s => (s.cpf ? maskCpf(s.cpf) : 'sem CPF') },
  { header: 'Plano',               accessor: s => s.plan ?? '' },
  { header: 'Dependentes',         accessor: s => s.dependents },
  { header: 'Situação na Medicar', accessor: s => (s.incomplete ? 'Cadastro incompleto' : s.medicarId ? 'Matrícula' : 'Aguardando inclusão') },
  { header: 'Matrícula',           accessor: s => (s.incomplete ? '' : s.medicarId ?? '') },
  { header: 'Motivo',              accessor: s => s.incomplete ?? '' },
  { header: 'Sincronizado em',     accessor: s => (!s.incomplete && s.medicarId && s.syncedAt ? dateTimeBR(s.syncedAt) : '') },
]

/** Vida escolhida para reenviar — o detalhe vai no texto da confirmação. */
type ResendTarget = { id: string; name: string; detail: string | null }

type StatusFilter = 'TODOS' | MedicarRequestStatus
type Tab = 'requests' | 'eligible'

/** Renderização incremental — uma rodada completa tem milhares de requisições. */
const PAGE_SIZE = 50

const normalize = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

export function IntegrationsMedicarPage() {
  const { can } = useAuthority()
  const canSync = can('integrations.medicar.sync')
  const canTest = can('integrations.medicar.test')
  const canDelete = can('integrations.medicar.delete')
  const canCheck = can('integrations.medicar.check')
  const canResend = can('integrations.medicar.resend')

  const [overview, setOverview] = useState<MedicarOverview | null>(null)
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [marks, setMarks] = useState<DatePickerMark[]>([])
  const [dayRuns, setDayRuns] = useState<MedicarSyncRunSummary[]>([])
  const [run, setRun] = useState<MedicarSyncRun | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingRun, setLoadingRun] = useState(false)

  const [tab, setTab] = useState<Tab>('requests')
  const [filter, setFilter] = useState<StatusFilter>('TODOS')
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [visible, setVisible] = useState(PAGE_SIZE)

  const [eligible, setEligible] = useState<MedicarEligible[]>([])
  const [eligibleTotal, setEligibleTotal] = useState(0)
  const [eligibleSearch, setEligibleSearch] = useState('')
  const [eligibleLoading, setEligibleLoading] = useState(false)

  const [syncOpen, setSyncOpen] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [delOpen, setDelOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [testOpen, setTestOpen] = useState(false)
  const [testPreset, setTestPreset] = useState<AsyncOption | null>(null)
  const [checkOpen, setCheckOpen] = useState(false)
  const [resendTarget, setResendTarget] = useState<ResendTarget | null>(null)
  const [resending, setResending] = useState(false)

  // ---------------------------------------------------------------- dados
  const loadOverview = useCallback(() => MedicarService.overview().then(setOverview), [])

  const loadMarks = useCallback((month: string) => {
    MedicarService.days(month)
      .then(days => setMarks(days.map(d => ({
        day: d.day,
        tone: d.failed ? 'error' : d.errors ? 'warn' : 'ok',
        title: `${d.runs} rodada(s)${d.failed ? ' — houve falha' : d.errors ? ' — com erros' : ''}`,
      }))))
      .catch(() => setMarks([]))
  }, [])

  const openRun = useCallback((id: string) => {
    setLoadingRun(true)
    setExpanded(new Set()); setVisible(PAGE_SIZE)
    MedicarService.run(id)
      .then(setRun)
      .catch(() => toast.error('Não foi possível carregar a sincronização.'))
      .finally(() => setLoadingRun(false))
  }, [])

  const loadDay = useCallback((day: string, silent = false) => {
    if (!silent) setLoading(true)
    MedicarService.runsOn(day)
      .then(list => {
        setDayRuns(list)
        // Sem rodada no dia: a tela mostra vazio em vez de um relatório de outro dia.
        if (list.length === 0) { setRun(null); return }
        setRun(prev => (prev && list.some(r => r.id === prev.id)) ? prev : null)
        if (!list.some(r => r.id === run?.id)) openRun(list[0].id)
      })
      .catch(() => toast.error('Não foi possível carregar as sincronizações.'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRun])

  useEffect(() => {
    loadOverview().catch(() => {})
    loadMarks(dayjs(date).format('YYYY-MM'))
    loadDay(date)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { loadDay(date) }, [date]) // eslint-disable-line react-hooks/exhaustive-deps

  // Enquanto uma rodada estiver em andamento, acompanha sozinha.
  useEffect(() => {
    if (run?.status !== 'EM_ANDAMENTO') return
    const t = setInterval(() => {
      openRun(run.id)
      loadDay(date, true)
      loadMarks(dayjs(date).format('YYYY-MM'))
      loadOverview().catch(() => {})
    }, 5000)
    return () => clearInterval(t)
  }, [run?.status, run?.id, date, openRun, loadDay, loadMarks, loadOverview])

  const loadEligible = useCallback((q: string) => {
    setEligibleLoading(true)
    MedicarService.eligible(q)
      .then(r => { setEligible(r.data); setEligibleTotal(r.total) })
      .catch(() => toast.error('Não foi possível carregar os associados.'))
      .finally(() => setEligibleLoading(false))
  }, [])

  useEffect(() => {
    if (tab !== 'eligible') return
    const t = setTimeout(() => loadEligible(eligibleSearch.trim()), 300)
    return () => clearTimeout(t)
  }, [tab, eligibleSearch, loadEligible])

  // ---------------------------------------------------------------- ações
  async function confirmSync() {
    setSyncing(true)
    try {
      const r = await MedicarService.sync()
      toast.success({ title: 'Sincronização iniciada', description: 'Roda em segundo plano; a tela acompanha sozinha.' })
      setSyncOpen(false)
      setDate(dayjs().format('YYYY-MM-DD'))
      setDayRuns(prev => [r, ...prev])
      openRun(r.id)
      loadMarks(dayjs().format('YYYY-MM'))
    } catch (e) {
      toast.error({ title: 'Não foi possível iniciar', description: extractApiError(e).message })
    } finally { setSyncing(false) }
  }

  async function confirmDelete() {
    if (!run) return
    setDeleting(true)
    try {
      await MedicarService.deleteRun(run.id)
      toast.success({ title: 'Relatório excluído', description: 'A rodada e suas requisições foram removidas.' })
      setDelOpen(false); setRun(null)
      loadDay(date); loadMarks(dayjs(date).format('YYYY-MM')); loadOverview().catch(() => {})
    } catch (e) {
      toast.error({ title: 'Não foi possível excluir', description: extractApiError(e).message })
    } finally { setDeleting(false) }
  }

  /** Mostra no relatório uma rodada que acabou de acontecer (teste, consulta, reenvio). */
  const showRun = useCallback((r: MedicarSyncRun | MedicarSyncRunSummary) => {
    setDate(dayjs().format('YYYY-MM-DD'))
    setDayRuns(prev => [r, ...prev.filter(x => x.id !== r.id)])
    if ('requests' in r) setRun(r)
    else openRun(r.id)
    setTab('requests')
    loadMarks(dayjs().format('YYYY-MM')); loadOverview().catch(() => {})
  }, [openRun, loadMarks, loadOverview])

  async function confirmResend() {
    if (!resendTarget) return
    setResending(true)
    try {
      const r = await MedicarService.resend(resendTarget.id)
      const req = r.requests.find(x => x.operation === 'CREATE') ?? r.requests[0]
      if (req?.status === 'SUCCESS') {
        toast.success({ title: 'Inclusão reenviada', description: `${resendTarget.name}: a Medicar abriu um novo protocolo. O relatório está na tela.` })
      } else {
        toast.error({ title: 'A Medicar não aceitou o reenvio', description: req?.errorMessage ?? r.errorMessage ?? 'Veja o relatório na tela.' })
      }
      setResendTarget(null)
      showRun(r)
    } catch (e) {
      toast.error({ title: 'Não foi possível reenviar', description: extractApiError(e).message })
    } finally { setResending(false) }
  }

  const toggle = (id: string) =>
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  // Requisições: já vêm em ordem alfabética do backend; aqui só filtra.
  // Requisições: já vêm em ordem alfabética do backend; aqui só filtra.
  const requests = useMemo(() => {
    const list = run?.requests ?? []
    const q = normalize(search.trim())
    const digits = q.replace(/\D/g, '')
    return list.filter((r) => {
      if (filter !== 'TODOS' && r.status !== filter) return false
      if (!q) return true
      return normalize(r.subscriberName ?? '').includes(q)
        || (digits !== '' && (r.subscriberCpf ?? '').includes(digits))
    })
  }, [run, filter, search])

  const loadTestOptions = async (q: string): Promise<AsyncOption[]> => {
    const r = await MedicarService.eligible(q)
    return r.data.map(s => ({ value: s.id, label: `${s.name}${s.cpf ? ` · ${maskCpf(s.cpf)}` : ''}` }))
  }

  const notConfigured = overview && !overview.configured && !overview.fake
  const noPlans = overview && overview.linkedPlans === 0

  return (
    <>
      <PageHeader
        title="Medicar"
        subtitle="Sincronização de beneficiários com a telemedicina Medicar (TOTVS)"
        crumbs={[{ label: 'Integrações' }, { label: 'Medicar' }]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setPreviewOpen(true)} iconLeft={<ListChecks size={15} />}>
              Prévia da sincronização
            </Button>
            {canCheck && (
              <Button variant="secondary" onClick={() => setCheckOpen(true)} iconLeft={<SearchCheck size={15} />}>
                Consultar protocolos
              </Button>
            )}
            {canTest && (
              <Button variant="secondary" onClick={() => { setTestPreset(null); setTestOpen(true) }} iconLeft={<FlaskConical size={15} />}>
                Testar integração
              </Button>
            )}
            {canSync && (
              <Button onClick={() => setSyncOpen(true)} iconLeft={<PlayCircle size={15} />}
                disabled={run?.status === 'EM_ANDAMENTO'}>
                Sincronizar agora
              </Button>
            )}
          </div>
        }
      />

      {/* Avisos que mudam o que a rotina faz */}
      {notConfigured && (
        <Banner tone="rose" icon={<ShieldAlert size={16} />}>
          <strong>Integração sem configuração.</strong> A rotina das 05:00 não envia nada até preencher
          {' '}{overview!.missing.join(', ')} em <Link to="/settings/integrations/medicar" className="underline">Configurações → Integrações → Medicar</Link>.
        </Banner>
      )}
      {overview?.fake && (
        <Banner tone="amber" icon={<AlertTriangle size={16} />}>
          <strong>Modo simulação:</strong> sem credenciais, nenhuma chamada sai para a Medicar e as matrículas gravadas são fictícias.
        </Banner>
      )}
      {noPlans && (
        <Banner tone="amber" icon={<AlertTriangle size={16} />}>
          <strong>Nenhum plano tem a Medicar no produto.</strong> Só entram na sincronização associados ativos em planos cujo
          produto inclua um serviço prestado pela Medicar — hoje isso é zero. O vínculo é feito em
          {' '}<Link to="/settings/commercial/products" className="underline">Configurações → Produtos</Link>.
        </Banner>
      )}

      {/* Resumo da integração */}
      <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label="Vidas na regra" value={String(overview?.livesCount ?? '—')} icon={<Users size={18} />}
          hint={overview
            ? `${overview.holdersCount} titular(es) · ${overview.dependentsCount} dependente(s) · ${overview.linkedPlans} plano(s)`
            : undefined} />
        <KpiCard label="Já na Medicar" value={String(overview?.syncedCount ?? '—')} icon={<CheckCircle2 size={18} />} accent="green"
          hint={overview ? `com matrícula · ${overview.awaitingCount} aguardando análise` : 'com matrícula'} />
        <KpiCard label="A bloquear" value={String(overview?.toBlockCount ?? '—')} icon={<MinusCircle size={18} />} accent={overview?.toBlockCount ? 'amber' : 'brand'}
          hint="saíram da regra e ainda constam lá" />
        <KpiCard label="Rotina" value={overview?.scheduleText ?? '—'} icon={<Plug size={18} />} accent="violet"
          hint={overview?.latestRun ? `última: ${dateTimeBR(overview.latestRun.createdAt)}` : 'nunca rodou'} />
      </div>

      {/* Dia + rodada */}
      <div className="mb-4 flex flex-col gap-3 rounded-md bg-white p-4 ring-1 ring-gray-200 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <DatePicker
            label="Dia da sincronização" value={date} onChange={setDate}
            marks={marks} onMonthChange={loadMarks} className="w-48"
            hint="Dias em verde têm relatório." />
          {dayRuns.length > 1 && (
            <div className="flex flex-col">
              <label className="mb-1 text-xs font-medium text-gray-700">Rodada do dia</label>
              <select
                value={run?.id ?? ''}
                onChange={e => openRun(e.target.value)}
                className="cursor-pointer rounded-md border border-gray-300 bg-white px-2.5 py-2 text-sm text-gray-700 focus:border-brand-500 focus:outline-none"
              >
                {dayRuns.map(r => (
                  <option key={r.id} value={r.id}>
                    {dayjs(r.createdAt).format('HH:mm')} — {kindLabel(r.kind, r.userName)} · {RUN_STATUS[r.status]?.label ?? r.status}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => { loadDay(date); loadOverview().catch(() => {}) }} loading={loading || loadingRun} iconLeft={<RefreshCw size={14} />}>
            Recarregar
          </Button>
          {run && canDelete && (
            <Button variant="danger" size="sm" onClick={() => setDelOpen(true)} iconLeft={<Trash2 size={14} />}>Excluir relatório</Button>
          )}
        </div>
      </div>

      <Tabs
        items={[
          { id: 'requests', label: 'Requisições da rodada', badge: run ? <Badge variant="gray">{run.totalCount}</Badge> : undefined },
          { id: 'eligible', label: 'Titulares elegíveis', badge: overview ? <Badge variant="gray">{overview.holdersCount}</Badge> : undefined },
        ]}
        active={tab}
        onChange={(id) => setTab(id as Tab)}
      />

      {tab === 'requests' && (
        <div className="mt-4">
          {loading && !run ? (
            <div className="text-sm text-gray-500">Carregando…</div>
          ) : !run ? (
            <EmptyState
              icon={<Activity size={22} />}
              title={`Nenhuma sincronização em ${dayjs(date).format('DD/MM/YYYY')}`}
              description="Escolha no calendário um dia marcado em verde, ou dispare uma sincronização agora."
            />
          ) : (
            <RunPanel
              run={run}
              filter={filter} setFilter={(f) => { setFilter(f); setVisible(PAGE_SIZE) }}
              search={search} setSearch={(s) => { setSearch(s); setVisible(PAGE_SIZE) }}
              requests={requests} visible={visible} setVisible={setVisible}
              expanded={expanded} toggle={toggle}
            />
          )}
        </div>
      )}

      {tab === 'eligible' && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="w-full max-w-sm">
              <Input value={eligibleSearch} onChange={e => setEligibleSearch(e.target.value)} placeholder="Filtrar por nome ou CPF" />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-xs text-gray-500">
                {eligibleLoading ? 'Carregando…' : `${eligible.length} de ${eligibleTotal} titular(es) na regra — ativos em plano com Medicar. Os dependentes de cada um vão junto na sincronização.`}
              </p>
              <Button variant="secondary" size="sm" iconLeft={<FileSpreadsheet size={14} />} disabled={eligibleLoading}
                onClick={() => {
                  if (!eligible.length) { toast.error('Nenhum registro para exportar.'); return }
                  exportToXlsx('medicar-titulares-elegiveis', eligible, ELIGIBLE_EXPORT_COLUMNS)
                }}>
                Exportar Excel
              </Button>
            </div>
          </div>

          {!eligibleLoading && eligible.length === 0 ? (
            <EmptyState icon={<Users size={22} />} title="Nenhum titular na regra"
              description={noPlans ? 'Nenhum plano tem a Medicar no produto. Faça o vínculo em Configurações → Produtos.' : 'Nenhum associado ativo corresponde ao filtro.'} />
          ) : (
            <div className="overflow-hidden rounded-md bg-white ring-1 ring-gray-200">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Associado</th>
                    <th className="px-3 py-2">Plano</th>
                    <th className="px-3 py-2 text-center">Dep.</th>
                    <th className="px-3 py-2">Situação na Medicar</th>
                    {canTest && <th className="px-3 py-2" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {eligible.map(s => (
                    <tr key={s.id} className="hover:bg-gray-50/60">
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-800">{s.name}</div>
                        <div className="text-[11px] text-gray-500">{s.cpf ? maskCpf(s.cpf) : 'sem CPF'}</div>
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{s.plan ?? '—'}</td>
                      <td className="px-3 py-2 text-center text-xs text-gray-600">{s.dependents}</td>
                      <td className="px-3 py-2">
                        {s.incomplete ? (
                          <Badge variant="amber">{s.incomplete}</Badge>
                        ) : s.medicarId ? (
                          <span className="inline-flex flex-wrap items-center gap-1.5">
                            <Badge variant="green">matrícula {s.medicarId}</Badge>
                            {s.syncedAt && <span className="text-[11px] text-gray-400">{dateTimeBR(s.syncedAt)}</span>}
                          </span>
                        ) : (
                          <Badge variant="gray">aguardando inclusão</Badge>
                        )}
                      </td>
                      {canTest && (
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="ghost" iconLeft={<FlaskConical size={13} />}
                            onClick={() => { setTestPreset({ value: s.id, label: `${s.name}${s.cpf ? ` · ${maskCpf(s.cpf)}` : ''}` }); setTestOpen(true) }}>
                            Testar
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={syncOpen}
        title="Sincronizar com a Medicar agora"
        description={`Serão processadas ${overview?.livesCount ?? 0} vida(s) na regra — ${overview?.holdersCount ?? 0} titular(es) e ${overview?.dependentsCount ?? 0} dependente(s) — mais ${overview?.toBlockCount ?? 0} exclusão(ões). Inclusões e alterações vão para a Medicar de verdade: é a mesma rotina das 05:00. Veja a prévia antes, se quiser conferir vida a vida.`}
        confirmLabel="Sincronizar"
        loading={syncing}
        onConfirm={() => void confirmSync()}
        onClose={() => setSyncOpen(false)}
      />

      <ConfirmDialog
        open={delOpen}
        title="Excluir relatório de sincronização"
        description="A rodada selecionada e todas as suas requisições serão removidas permanentemente. Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        variant="danger"
        loading={deleting}
        onConfirm={() => void confirmDelete()}
        onClose={() => setDelOpen(false)}
      />

      <PreviewSheet open={previewOpen} onClose={() => setPreviewOpen(false)}
        canCheck={canCheck} canResend={canResend} onRun={showRun} onResend={setResendTarget} />

      {checkOpen && (
        <CheckModal
          awaitingCount={overview?.awaitingCount ?? 0}
          canResend={canResend}
          onClose={() => setCheckOpen(false)}
          onRun={showRun}
          onResend={setResendTarget}
          onStartedAll={(r) => { setCheckOpen(false); showRun(r) }}
        />
      )}

      {testOpen && (
        <TestModal
          preset={testPreset}
          loadOptions={loadTestOptions}
          onClose={() => setTestOpen(false)}
          onSent={showRun}
        />
      )}

      {/* Por último: abre por cima da gaveta e do modal de consulta. */}
      <ConfirmDialog
        open={resendTarget !== null}
        title="Reenviar inclusão à Medicar"
        description={resendTarget
          ? `${resendTarget.name}: a inclusão vai de novo para a Medicar agora, mesmo com protocolo em análise${resendTarget.detail ? ` (${resendTarget.detail})` : ''}. Se o protocolo antigo existir lá, a Medicar fica com dois — use quando tiver certeza de que ele não vale. Consultar o protocolo antes não envia nada.`
          : ''}
        confirmLabel="Reenviar"
        loading={resending}
        onConfirm={() => void confirmResend()}
        onClose={() => setResendTarget(null)}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Painel de uma rodada: resumo + filtro + lista de requisições
// ---------------------------------------------------------------------------
function RunPanel({ run, filter, setFilter, search, setSearch, requests, visible, setVisible, expanded, toggle }: {
  run: MedicarSyncRun
  filter: StatusFilter; setFilter: (f: StatusFilter) => void
  search: string; setSearch: (s: string) => void
  requests: MedicarSyncRequest[]
  visible: number; setVisible: (fn: (v: number) => number) => void
  expanded: Set<string>; toggle: (id: string) => void
}) {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
        <Badge variant={RUN_STATUS[run.status]?.variant ?? 'gray'}>{RUN_STATUS[run.status]?.label ?? run.status}</Badge>
        <Badge variant={run.kind === 'FULL' ? 'blue' : 'violet'}>{kindLabel(run.kind, run.userName)}</Badge>
        {run.kind !== 'FULL' && run.userName && <span className="text-xs text-gray-500">por {run.userName}</span>}
        {run.fake && <Badge variant="amber">simulação</Badge>}
        <span className="text-gray-400">·</span>
        <span>
          {run.finishedAt
            ? <>Finalizada em <strong className="text-gray-700">{dateTimeBR(run.finishedAt)}</strong></>
            : <>Iniciada em <strong className="text-gray-700">{dateTimeBR(run.startedAt ?? run.createdAt)}</strong></>}
        </span>
      </div>

      {run.status === 'FALHOU' && run.errorMessage && (
        <Banner tone="rose" icon={<AlertTriangle size={16} />}>
          <strong>A rodada foi interrompida:</strong> {run.errorMessage}
        </Banner>
      )}

      {run.kind === 'CHECK' ? (
        // Consulta: os contadores da rodada ganham outro sentido (ver MedicarCheckProtocolsJob).
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <KpiCard label="Consultadas" value={`${run.totalCount} de ${run.eligibleCount}`} hint="só leitura — nada foi enviado" />
          <KpiCard label="Com matrícula" accent="green" value={run.createdCount} hint="aprovadas; matrícula gravada" />
          <KpiCard label="Em análise" value={run.updatedCount} hint="protocolo ainda sem aprovação" />
          <KpiCard label="Sem protocolo lá" accent={run.skippedCount > 0 ? 'amber' : 'brand'} value={run.skippedCount} hint="entram no reenvio" />
          <KpiCard label="Erros" accent={run.errorCount > 0 ? 'rose' : 'brand'} value={run.errorCount} hint="voltam na próxima consulta" />
        </div>
      ) : (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-6">
        <KpiCard label="Vidas na regra" value={run.eligibleCount} hint="titulares + dependentes" />
        <KpiCard label="Requisições" value={run.totalCount} hint="enviadas nesta rodada" />
        <KpiCard label="Sucesso" accent="green" value={run.successCount} />
        <KpiCard label="Erros" accent={run.errorCount > 0 ? 'rose' : 'brand'} value={run.errorCount} />
        <KpiCard label="Pulados" accent={run.skippedCount > 0 ? 'amber' : 'brand'} value={run.skippedCount} hint="cadastro incompleto" />
        <KpiCard label="Incluídos" value={run.createdCount} hint={`${run.updatedCount} alterado(s) · ${run.blockedCount} bloqueado(s)`} />
      </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)} placeholder="Filtrar por nome ou CPF"
            className="w-full rounded-md bg-white py-2 pl-9 pr-3 text-sm ring-1 ring-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <div className="inline-flex overflow-hidden rounded-md border border-gray-200 text-xs">
          {((run.kind === 'CHECK' ? ['TODOS', 'SUCCESS', 'ERROR'] : ['TODOS', 'SUCCESS', 'ERROR', 'SKIPPED']) as StatusFilter[]).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`cursor-pointer px-3 py-1.5 font-medium transition ${filter === f ? 'bg-brand-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              {f === 'TODOS' ? 'Todas' : f === 'SUCCESS' ? `${run.kind === 'CHECK' ? 'Consultadas' : 'Sucesso'} (${run.successCount})` : f === 'ERROR' ? `Erros (${run.errorCount})` : `Pulados (${run.skippedCount})`}
            </button>
          ))}
        </div>
      </div>

      {requests.length === 0 ? (
        <EmptyState title="Nenhuma requisição" description="Esta rodada não tem requisições com este filtro." />
      ) : (
        <>
          <div className="space-y-2">
            {requests.slice(0, visible).map(req => (
              <RequestRow key={req.id} req={req} open={expanded.has(req.id)} onToggle={() => toggle(req.id)} />
            ))}
          </div>
          {visible < requests.length && (
            <div className="flex items-center justify-center gap-3">
              <span className="text-xs text-gray-500">Exibindo {visible} de {requests.length}</span>
              <Button variant="secondary" onClick={() => setVisible(v => v + PAGE_SIZE)}>Carregar mais</Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function RequestRow({ req, open, onToggle }: { req: MedicarSyncRequest; open: boolean; onToggle: () => void }) {
  const ok = req.status === 'SUCCESS'
  const skipped = req.status === 'SKIPPED'
  const op = OPERATION[req.operation] ?? { label: req.operation, variant: 'blue' as const }
  // Consulta: o texto guardado é o resultado (aprovado, em análise…), não só erro.
  const lookup = req.operation === 'LOOKUP'

  return (
    <div className={`rounded-md border bg-white ${ok ? 'border-gray-200' : skipped ? 'border-amber-200' : 'border-rose-200'}`}>
      <button onClick={onToggle} className="flex w-full cursor-pointer items-center gap-3 px-3.5 py-3 text-left">
        {open ? <ChevronDown size={16} className="shrink-0 text-gray-400" /> : <ChevronRight size={16} className="shrink-0 text-gray-400" />}
        {ok ? <CheckCircle2 size={18} className="shrink-0 text-emerald-500" />
          : skipped ? <MinusCircle size={18} className="shrink-0 text-amber-500" />
          : <XCircle size={18} className="shrink-0 text-rose-500" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="break-words text-sm font-medium text-gray-900">{req.subscriberName ?? '—'}</span>
            <Badge variant={op.variant}>{op.label}</Badge>
          </div>
          <p className="break-words text-xs text-gray-500">
            {req.subscriberCpf ? `CPF ${maskCpf(req.subscriberCpf)} · ` : ''}{skipped || lookup ? req.errorMessage : (req.endpoint ?? '—')}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {req.httpStatus != null && <span className="text-xs text-gray-400">HTTP {req.httpStatus}</span>}
          <Badge variant={ok ? 'green' : skipped ? 'amber' : 'red'}>{ok ? (lookup ? 'Consultada' : 'Sucesso') : skipped ? 'Pulado' : 'Erro'}</Badge>
        </div>
      </button>

      {open && (
        <div className="space-y-3 border-t border-gray-100 px-3.5 py-3">
          {!ok && req.errorMessage && (
            <div className={`flex items-start gap-2 rounded-md px-3 py-2 text-sm ${skipped ? 'bg-amber-50 text-amber-800' : 'bg-rose-50 text-rose-700'}`}>
              <AlertTriangle size={15} className="mt-0.5 shrink-0" />
              <span>{req.errorMessage}</span>
            </div>
          )}
          {ok && lookup && req.errorMessage && (
            <div className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">{req.errorMessage}</div>
          )}
          {!skipped && <JsonBlock title={lookup ? 'Chamadas feitas' : 'Payload enviado'} json={req.requestPayload} />}
          {!skipped && <JsonBlock title={lookup ? 'Respostas da Medicar' : 'Resposta recebida'} json={req.responseBody} />}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Teste: conexão + envio de UM associado, com payload e resposta na tela
// ---------------------------------------------------------------------------
function TestModal({ preset, loadOptions, onClose, onSent }: {
  preset: AsyncOption | null
  loadOptions: (q: string) => Promise<AsyncOption[]>
  onClose: () => void
  onSent: (run: MedicarSyncRun) => void
}) {
  const [subscriberId, setSubscriberId] = useState(preset?.value ?? '')
  const [subscriberLabel, setSubscriberLabel] = useState(preset?.label ?? '')
  const [dryRun, setDryRun] = useState(true)
  const [busy, setBusy] = useState(false)
  const [auth, setAuth] = useState<MedicarAuthTest | null>(null)
  const [result, setResult] = useState<MedicarSyncRun | MedicarDryRun | null>(null)

  async function testAuth() {
    setBusy(true); setAuth(null)
    try { setAuth(await MedicarService.testAuth()) }
    catch (e) { toast.error({ title: 'Falha ao testar', description: extractApiError(e).message }) }
    finally { setBusy(false) }
  }

  async function send() {
    if (!subscriberId) { toast.error({ title: 'Escolha um associado', description: 'Só entram os que estão na regra (ativos em plano com Medicar).' }); return }
    setBusy(true); setResult(null)
    try {
      const r = await MedicarService.test(subscriberId, dryRun)
      setResult(r)
      if (!('dryRun' in r)) onSent(r)
    } catch (e) {
      toast.error({ title: 'Não foi possível testar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  const sentRun = result && !('dryRun' in result) ? result : null
  const dry = result && 'dryRun' in result ? result : null

  return (
    <Modal open onClose={onClose} title="Testar a integração com a Medicar" size="lg"
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Fechar</Button>
        <Button variant="secondary" onClick={() => void testAuth()} loading={busy} iconLeft={<Plug size={14} />}>Testar conexão</Button>
        <Button onClick={() => void send()} loading={busy} iconLeft={<FlaskConical size={14} />}>
          {dryRun ? 'Montar payload' : 'Enviar associado'}
        </Button>
      </>}>
      <div className="space-y-4">
        <p className="text-xs text-gray-500">
          Dois passos: <strong>Testar conexão</strong> autentica na Medicar e consulta o contrato configurado.
          <strong> Enviar associado</strong> faz a inclusão (ou alteração) de uma pessoa só e mostra exatamente o que
          foi enviado e o que voltou — a mesma chamada que a rotina das 05:00 faz para todos.
        </p>

        {auth && (
          <div className="space-y-3">
            <div className={`flex flex-wrap items-center justify-between gap-2 rounded-md px-3 py-2.5 text-xs ring-1 ${auth.auth.ok ? 'bg-emerald-50 text-emerald-900 ring-emerald-200' : 'bg-rose-50 text-rose-900 ring-rose-200'}`}>
              <div>
                <p className="font-medium">
                  {auth.auth.ok ? `Autenticação OK${auth.fake ? ' (simulação)' : ''} — token ${auth.auth.tokenHint}` : 'Autenticação falhou'}
                </p>
                <p className="mt-0.5 break-all">{auth.auth.error ?? auth.auth.endpoint}</p>
                {auth.contract && (
                  <p className="mt-1">Consulta de contrato: {auth.contract.ok ? 'OK' : `falhou — ${auth.contract.error}`}</p>
                )}
              </div>
              <CopyButton text={diagnosticoTexto(auth)} label="Copiar diagnóstico" />
            </div>

            {auth.auth.exchange && <ExchangeBlock title="1 · Autenticação (token)" ex={auth.auth.exchange} />}
            {auth.contract?.exchange && <ExchangeBlock title="2 · Consulta de contrato" ex={auth.contract.exchange} />}
          </div>
        )}

        <AsyncSelect
          label="Associado" required value={subscriberId} selectedLabel={subscriberLabel}
          loadOptions={loadOptions}
          onChange={(v, o) => { setSubscriberId(v); setSubscriberLabel(o?.label ?? ''); setResult(null) }}
          placeholder="Busque um associado na regra" searchPlaceholder="Nome ou CPF"
        />

        <Toggle checked={dryRun} onChange={setDryRun}
          label={dryRun ? 'Só montar o payload (não envia nada para a Medicar)' : 'Enviar de verdade para a Medicar'} />

        {dry && (
          <div className="space-y-2">
            <div className={`rounded-md px-3 py-2 text-xs ring-1 ${dry.auth.ok ? 'bg-emerald-50 text-emerald-900 ring-emerald-200' : 'bg-rose-50 text-rose-900 ring-rose-200'}`}>
              {dry.auth.ok ? 'Autenticação OK.' : `Autenticação falhou: ${dry.auth.error}`} Operação que seria feita: <strong>{OPERATION[dry.operation]?.label}</strong>. Nada foi enviado.
            </div>
            <JsonBlock title="Payload que seria enviado" json={dry.payload} />
          </div>
        )}

        {sentRun && (
          <div className="space-y-2">
            <div className={`rounded-md px-3 py-2 text-xs ring-1 ${sentRun.status === 'CONCLUIDO' && sentRun.errorCount === 0 ? 'bg-emerald-50 text-emerald-900 ring-emerald-200' : 'bg-rose-50 text-rose-900 ring-rose-200'}`}>
              {sentRun.status === 'FALHOU' ? `A rodada falhou: ${sentRun.errorMessage}` : `${sentRun.successCount} requisição(ões) com sucesso, ${sentRun.errorCount} com erro, ${sentRun.skippedCount} pulada(s).`}
              {' '}O relatório completo já está na tela, atrás desta janela.
            </div>
            {sentRun.requests.map(r => (
              <div key={r.id} className="space-y-2 rounded-md border border-gray-200 p-3">
                <div className="flex items-center gap-2 text-xs">
                  <Badge variant={OPERATION[r.operation]?.variant ?? 'blue'}>{OPERATION[r.operation]?.label ?? r.operation}</Badge>
                  <Badge variant={r.status === 'SUCCESS' ? 'green' : r.status === 'SKIPPED' ? 'amber' : 'red'}>{r.status === 'SUCCESS' ? 'Sucesso' : r.status === 'SKIPPED' ? 'Pulado' : 'Erro'}</Badge>
                  {r.httpStatus != null && <span className="text-gray-400">HTTP {r.httpStatus}</span>}
                  {r.errorMessage && <span className="text-rose-700">{r.errorMessage}</span>}
                </div>
                {r.requestPayload && <JsonBlock title="Payload enviado" json={r.requestPayload} />}
                {r.responseBody && <JsonBlock title="Resposta recebida" json={r.responseBody} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Consulta de protocolos: vidas escolhidas (na hora) ou todos os aguardando
// ---------------------------------------------------------------------------
const MAX_SELECTED = 20

function personSituation(p: MedicarPerson): string {
  if (p.medicarId) return `matrícula ${p.medicarId}`
  if (!p.protocolAt) return 'nunca enviada'
  const partes = [`enviada em ${dayjs(p.protocolAt).format('DD/MM/YYYY')}`]
  partes.push(p.protocolNumber ? `protocolo ${p.protocolNumber}` : 'sem nº de protocolo guardado')
  if (p.protocolLabel) partes.push(p.protocolLabel)
  if (p.checkedAt) partes.push(`conferida em ${dateTimeBR(p.checkedAt)}`)
  return partes.join(' · ')
}

function CheckModal({ awaitingCount, canResend, onClose, onRun, onResend, onStartedAll }: {
  awaitingCount: number
  canResend: boolean
  onClose: () => void
  onRun: (run: MedicarSyncRun) => void
  onResend: (target: ResendTarget) => void
  onStartedAll: (run: MedicarSyncRunSummary) => void
}) {
  const [q, setQ] = useState('')
  const [people, setPeople] = useState<MedicarPerson[]>([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<Map<string, MedicarPerson>>(new Map())
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<MedicarSyncRun | null>(null)
  const [confirmAll, setConfirmAll] = useState(false)

  const load = useCallback((term: string) => {
    setLoading(true)
    MedicarService.people(term)
      .then(setPeople)
      .catch(() => toast.error('Não foi possível buscar as vidas.'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), q ? 350 : 0)
    return () => clearTimeout(t)
  }, [q, load])

  function toggle(p: MedicarPerson) {
    setSelected(prev => {
      const n = new Map(prev)
      if (n.has(p.id)) n.delete(p.id)
      else if (n.size < MAX_SELECTED) n.set(p.id, p)
      else toast.error(`Até ${MAX_SELECTED} vidas por consulta. Para todas, use "Consultar todos os aguardando".`)
      return n
    })
  }

  async function checkSelected() {
    setBusy(true); setResult(null)
    try {
      const r = await MedicarService.check([...selected.keys()])
      setResult(r); onRun(r)
      setSelected(new Map())
      load(q.trim()) // a situação de cada vida mudou
    } catch (e) {
      toast.error({ title: 'Não foi possível consultar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  async function checkAll() {
    setBusy(true)
    try {
      const r = await MedicarService.checkAwaiting()
      toast.success({ title: 'Consulta iniciada', description: `${awaitingCount} vida(s) em segundo plano. O relatório atualiza sozinho.` })
      onStartedAll(r)
    } catch (e) {
      toast.error({ title: 'Não foi possível iniciar', description: extractApiError(e).message })
    } finally { setBusy(false); setConfirmAll(false) }
  }

  return (
    <Modal open onClose={onClose} title="Consultar protocolos na Medicar" size="lg"
      description="Pergunta à Medicar como está cada protocolo de inclusão. Só consulta — nada é enviado. Aprovado: a matrícula é gravada."
      footer={<>
        <Button variant="ghost" onClick={onClose} disabled={busy}>Fechar</Button>
        <Button onClick={() => void checkSelected()} loading={busy} disabled={selected.size === 0} iconLeft={<SearchCheck size={14} />}>
          Consultar selecionadas{selected.size > 0 ? ` (${selected.size})` : ''}
        </Button>
      </>}>
      <div className="space-y-4">
        {/* Todos os aguardando */}
        <div className="flex flex-col gap-2 rounded-md bg-gray-50 px-3 py-2.5 ring-1 ring-gray-200 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xs text-gray-600">
            <p className="font-medium text-gray-800">Todos os aguardando análise — {awaitingCount} vida(s)</p>
            <p>Protocolo enviado e ainda sem matrícula. Roda em segundo plano, em lotes; acompanhe no relatório da tela.</p>
          </div>
          {confirmAll ? (
            <div className="flex shrink-0 gap-1.5">
              <Button size="sm" variant="ghost" onClick={() => setConfirmAll(false)} disabled={busy}>Cancelar</Button>
              <Button size="sm" onClick={() => void checkAll()} loading={busy}>Confirmar {awaitingCount}</Button>
            </div>
          ) : (
            <Button size="sm" variant="secondary" className="shrink-0" disabled={awaitingCount === 0 || busy}
              onClick={() => setConfirmAll(true)} iconLeft={<SearchCheck size={13} />}>
              Consultar todos
            </Button>
          )}
        </div>

        {/* Resultado da última consulta */}
        {result && (
          <div className="space-y-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Resultado</p>
            {result.status === 'FALHOU' && (
              <div className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800">{result.errorMessage}</div>
            )}
            {result.requests.map(r => (
              <div key={r.id} className={`flex items-start gap-2 rounded-md px-3 py-2 text-xs ring-1 ${r.status === 'SUCCESS' ? 'bg-white ring-gray-200' : 'bg-rose-50 ring-rose-200'}`}>
                {r.status === 'SUCCESS' ? <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-500" /> : <XCircle size={14} className="mt-0.5 shrink-0 text-rose-500" />}
                <span><strong className="text-gray-800">{r.subscriberName}</strong> — {r.errorMessage}</span>
              </div>
            ))}
            <p className="text-[11px] text-gray-400">As chamadas e respostas completas estão no relatório, atrás desta janela.</p>
          </div>
        )}

        {/* Vidas escolhidas à mão */}
        <div className="space-y-2">
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar vida por nome ou CPF (titular ou dependente)"
              className="w-full rounded-md bg-white py-2 pl-9 pr-3 text-sm ring-1 ring-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500" />
          </div>
          <p className="text-[11px] text-gray-500">
            {q.trim() ? 'Marque até 20 vidas.' : 'Sem busca: quem espera há mais tempo aparece primeiro.'}
            {selected.size > 0 && <> · {selected.size} selecionada(s)</>}
          </p>

          <div className="max-h-80 space-y-1.5 overflow-auto">
            {loading && people.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500">Buscando…</p>
            ) : people.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500">Nenhuma vida encontrada.</p>
            ) : people.map(p => {
              const marcada = selected.has(p.id)
              return (
                <div key={p.id} className={`flex items-start gap-3 rounded-md border px-3 py-2 ${marcada ? 'border-brand-300 bg-brand-50/40' : 'border-gray-200 bg-white'}`}>
                  <input type="checkbox" checked={marcada} onChange={() => toggle(p)} className="mt-1 cursor-pointer accent-brand-600" aria-label={`Selecionar ${p.name}`} />
                  <div className="min-w-0 flex-1 cursor-pointer" onClick={() => toggle(p)}>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="break-words text-sm font-medium text-gray-900">{p.name}</span>
                      <Badge variant={p.type === 'TITULAR' ? 'blue' : 'gray'}>{p.type === 'TITULAR' ? 'titular' : 'dependente'}</Badge>
                      {p.medicarId && <Badge variant="green">na Medicar</Badge>}
                      {p.protocolStatus === 'NAO_LOCAL' && <Badge variant="amber">sem protocolo lá</Badge>}
                    </div>
                    <p className="text-[11px] text-gray-500">
                      {p.cpf ? `CPF ${maskCpf(p.cpf)}` : 'sem CPF'}{p.holderName && <> · titular: {p.holderName}</>}
                    </p>
                    <p className="text-[11px] text-gray-600">{personSituation(p)}</p>
                  </div>
                  {canResend && !p.medicarId && (
                    <Button size="xs" variant="ghost" className="shrink-0" iconLeft={<Send size={12} />}
                      onClick={() => onResend({ id: p.id, name: p.name, detail: personSituation(p) })}>
                      Reenviar
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Prévia: o que a próxima rodada faria, sem enviar nada
// ---------------------------------------------------------------------------
function PreviewSheet({ open, onClose, canCheck, canResend, onRun, onResend }: {
  open: boolean; onClose: () => void
  canCheck: boolean; canResend: boolean
  onRun: (run: MedicarSyncRun) => void
  onResend: (target: ResendTarget) => void
}) {
  const [data, setData] = useState<MedicarPreview | null>(null)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [action, setAction] = useState<MedicarPreviewAction | ''>('')
  const [visible, setVisible] = useState(PAGE_SIZE)

  // A prévia monta o payload de cada vida para comparar com o último envio:
  // é cara o bastante para não refazer a cada tecla digitada.
  const load = useCallback((q: string, act: MedicarPreviewAction | '', fresh = false) => {
    setLoading(true)
    MedicarService.preview(q, act, fresh)
      .then((r) => { setData(r); setVisible(PAGE_SIZE) })
      .catch((e) => toast.error({ title: 'Não foi possível calcular a prévia', description: extractApiError(e).message }))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => load(search.trim(), action), search ? 400 : 0)
    return () => clearTimeout(t)
  }, [open, search, action, load])

  const s = data?.summary
  const countOf: Record<MedicarPreviewAction, number | undefined> = {
    CREATE: s?.create, AWAITING: s?.awaiting, UPDATE: s?.update, UNCHANGED: s?.unchanged,
    BLOCK: s?.block, UNBLOCK: s?.unblock, MANUAL: s?.manual, INCOMPLETE: s?.incomplete,
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      size="3xl"
      title="Prévia da sincronização"
      subtitle={data
        ? `Foto de ${dateTimeBR(data.computedAt)} — nada é enviado à Medicar nesta tela.`
        : 'O que a próxima rodada faria. Nada é enviado à Medicar nesta tela.'}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Fechar</Button>
        <Button variant="secondary" onClick={() => load(search.trim(), action, true)} loading={loading} iconLeft={<RefreshCw size={14} />}>
          Recalcular
        </Button>
      </>}
    >
      <div className="space-y-4">
        {/* Números da base inteira — não mudam com o filtro. */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <KpiCard label="Vidas na regra" value={s?.inRule ?? '—'} icon={<Users size={18} />}
            hint={s
              ? `${s.holders} titular(es) · ${s.dependents} dependente(s)${s.analyzed > s.inRule ? ` · +${s.analyzed - s.inRule} fora da regra na lista (bloqueio/exclusão)` : ''}`
              : undefined} />
          <KpiCard label="Serão enviadas" value={s?.sending ?? '—'} icon={<PlayCircle size={18} />} accent="violet"
            hint="inclusões + alterações + bloqueios + desbloqueios" />
          <KpiCard label="Sem alteração" value={s?.unchanged ?? '—'} icon={<MinusCircle size={18} />}
            hint="não geram requisição" />
          <KpiCard label="Primeira sincronização" value={s?.create ?? '—'} icon={<UserPlus size={18} />} accent="green" />
          <KpiCard label="Aguardando análise" value={s?.awaiting ?? '—'} icon={<Clock size={18} />}
            hint="protocolo na Medicar, sem matrícula ainda" />
          <KpiCard label="Alteração cadastral" value={s?.update ?? '—'} icon={<RefreshCw size={18} />} accent="violet" />
          <KpiCard label="Bloqueios" value={s?.block ?? '—'} icon={<UserMinus size={18} />} accent={s?.block ? 'amber' : 'brand'}
            hint={s?.unblock ? `${s.unblock} desbloqueio(s)` : 'família inteira, pelo titular'} />
        </div>

        {(s?.incomplete ?? 0) > 0 && (
          <div className="flex items-start gap-2 rounded-md bg-rose-50 px-3 py-2.5 text-xs text-rose-900 ring-1 ring-rose-200">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>
              <strong>{s!.incomplete} vida(s) com cadastro incompleto</strong> não serão enviadas — a Medicar exige
              CPF válido (inclusive de menores), data de nascimento, sexo e nome no padrão ANS. Filtre por
              “Cadastro incompleto” abaixo para ver quem é e o que falta.
            </span>
          </div>
        )}

        {(s?.manual ?? 0) > 0 && (
          <div className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2.5 text-xs text-amber-900 ring-1 ring-amber-200">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>
              <strong>{s!.manual} dependente(s) inativado(s) com o titular ativo.</strong> A API da Medicar só bloqueia a
              família inteira, pelo titular — a exclusão desses precisa ser pedida à Medicar. Filtre por “Exclusão manual”.
            </span>
          </div>
        )}

        <div className="space-y-2">
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtrar por nome ou CPF"
              className="w-full rounded-md bg-white py-2 pl-9 pr-3 text-sm ring-1 ring-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <FilterChip active={action === ''} onClick={() => setAction('')} label="Todas" count={s?.analyzed} />
            {(Object.keys(PREVIEW_ACTION) as MedicarPreviewAction[]).map((key) => (
              <FilterChip
                key={key}
                active={action === key}
                onClick={() => setAction(action === key ? '' : key)}
                label={PREVIEW_ACTION[key].label}
                count={countOf[key]}
              />
            ))}
          </div>
        </div>

        {loading && !data ? (
          <p className="py-8 text-center text-sm text-gray-500">Calculando…</p>
        ) : (data?.rows.length ?? 0) === 0 ? (
          <EmptyState title="Nenhuma vida com este filtro"
            description={s && s.analyzed === 0 ? 'Nenhum associado está na regra da Medicar hoje.' : 'Ajuste o filtro para ver outras vidas.'} />
        ) : (
          <>
            <p className="text-xs text-gray-500">
              {data!.filtered} vida(s) no filtro
              {/* "Todas" soma as vidas na regra e as que saíram dela e ainda constam lá. */}
              {action === '' && !search.trim() && s && s.analyzed > s.inRule && (
                <> ({s.inRule} na regra + {s.analyzed - s.inRule} fora da regra, a bloquear ou excluir)</>
              )}
              {data!.capped && <> — exibindo as {data!.cap} primeiras</>}
            </p>
            <div className="space-y-1.5">
              {data!.rows.slice(0, visible).map((row) => (
                <PreviewRow key={`${row.id}-${row.action}`} row={row}
                  canCheck={canCheck} canResend={canResend} onRun={onRun} onResend={onResend} />
              ))}
            </div>
            {visible < data!.rows.length && (
              <div className="flex items-center justify-center gap-3">
                <span className="text-xs text-gray-500">Exibindo {visible} de {data!.rows.length}</span>
                <Button variant="secondary" onClick={() => setVisible((v) => v + PAGE_SIZE)}>Carregar mais</Button>
              </div>
            )}
          </>
        )}
      </div>
    </Sheet>
  )
}

function PreviewRow({ row, canCheck, canResend, onRun, onResend }: {
  row: MedicarPreviewRow
  canCheck: boolean; canResend: boolean
  onRun: (run: MedicarSyncRun) => void
  onResend: (target: ResendTarget) => void
}) {
  const cfg = PREVIEW_ACTION[row.action]
  const Icon = cfg.icon
  const [checking, setChecking] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  // Consultar/reenviar só faz sentido para quem ainda não tem matrícula.
  const pending = !row.medicarId && (row.action === 'AWAITING' || row.action === 'CREATE')

  async function check() {
    setChecking(true)
    try {
      const r = await MedicarService.check([row.id])
      const req = r.requests[0]
      setResult(req?.errorMessage ?? r.errorMessage ?? 'Consulta feita.')
      onRun(r)
    } catch (e) {
      toast.error({ title: 'Não foi possível consultar', description: extractApiError(e).message })
    } finally { setChecking(false) }
  }

  return (
    <div className="flex items-start gap-3 rounded-md border border-gray-200 bg-white px-3 py-2.5">
      <Icon size={16} className="mt-0.5 shrink-0 text-gray-400" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="break-words text-sm font-medium text-gray-900">{row.name}</span>
          <Badge variant={row.type === 'TITULAR' ? 'blue' : 'gray'}>{row.type === 'TITULAR' ? 'titular' : 'dependente'}</Badge>
          <Badge variant={cfg.variant}>{cfg.short}</Badge>
        </div>
        <p className="mt-0.5 text-[11px] text-gray-500">
          {row.cpf ? `CPF ${maskCpf(row.cpf)}` : 'sem CPF'}
          {row.holderName && <> · titular: {row.holderName}</>}
          {row.plan && <> · {row.plan}</>}
        </p>
        <p className="mt-1 text-xs text-gray-600">{row.reason}</p>
        {result && (
          <p className="mt-1 flex items-start gap-1.5 rounded bg-brand-50 px-2 py-1 text-xs text-brand-700">
            <SearchCheck size={13} className="mt-0.5 shrink-0" />{result}
          </p>
        )}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1.5 text-right text-[11px] text-gray-400">
        <div>
          {row.medicarId ? <div className="font-mono">{row.medicarId}</div> : <span>sem matrícula</span>}
          {row.syncedAt && <div>{dateTimeBR(row.syncedAt)}</div>}
        </div>
        {pending && (canCheck || canResend) && (
          <div className="flex gap-1">
            {canCheck && (
              <Button size="xs" variant="secondary" loading={checking} onClick={() => void check()} iconLeft={<SearchCheck size={12} />}>
                Consultar
              </Button>
            )}
            {canResend && (
              <Button size="xs" variant="ghost" onClick={() => onResend({ id: row.id, name: row.name, detail: row.reason })} iconLeft={<Send size={12} />}>
                Reenviar
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function FilterChip({ active, onClick, label, count }: { active: boolean; onClick: () => void; label: string; count?: number }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer rounded-full px-3 py-1 text-xs font-medium ring-1 transition ${
        active ? 'bg-brand-600 text-white ring-brand-600' : 'bg-white text-gray-600 ring-gray-200 hover:bg-gray-50'
      }`}
    >
      {label}
      {count !== undefined && <span className={active ? 'ml-1.5 text-white/80' : 'ml-1.5 text-gray-400'}>{count}</span>}
    </button>
  )
}

/**
 * Retrato de uma chamada HTTP à Medicar.
 *
 * Existe porque a autenticação vem sendo recusada e a mensagem de erro sozinha
 * não diz nada ao time deles: eles precisam ver a URL exata, o método, os
 * cabeçalhos, o status e o corpo da resposta. A senha vem mascarada do backend.
 */
function ExchangeBlock({ title, ex }: { title: string; ex: MedicarExchange }) {
  const ok = ex.httpStatus !== null && ex.httpStatus >= 200 && ex.httpStatus < 300

  return (
    <div className="overflow-hidden rounded-md ring-1 ring-gray-200">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2">
        <span className="text-xs font-semibold text-gray-700">{title}</span>
        <span className="flex items-center gap-2 text-[11px]">
          {ex.httpStatus !== null && (
            <Badge variant={ok ? 'green' : 'red'}>HTTP {ex.httpStatus}</Badge>
          )}
          {ex.durationMs !== null && <span className="text-gray-400">{ex.durationMs} ms</span>}
          <CopyButton text={exchangeTexto(title, ex)} label="Copiar" />
        </span>
      </div>

      <div className="space-y-2.5 p-3">
        <Campo label="Requisição">
          <code className="block break-all text-[11px] text-gray-800">{ex.method} {ex.url}</code>
        </Campo>

        {Object.keys(ex.requestHeaders ?? {}).length > 0 && (
          <Campo label="Cabeçalhos enviados"><Linhas obj={ex.requestHeaders} /></Campo>
        )}
        {ex.requestQuery && Object.keys(ex.requestQuery).length > 0 && (
          <Campo label="Parâmetros da query"><Linhas obj={ex.requestQuery} /></Campo>
        )}
        {ex.requestBody != null && (
          <JsonBlock title="Corpo enviado" json={JSON.stringify(ex.requestBody, null, 2)} />
        )}
        {Object.keys(ex.responseHeaders ?? {}).length > 0 && (
          <Campo label="Cabeçalhos da resposta"><Linhas obj={ex.responseHeaders} /></Campo>
        )}
        <JsonBlock title="Corpo da resposta" json={formatarCorpo(ex.responseBody)} />
        {ex.error && (
          <div className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-800">{ex.error}</div>
        )}
      </div>
    </div>
  )
}

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      {children}
    </div>
  )
}

function Linhas({ obj }: { obj: Record<string, string> }) {
  return (
    <div className="space-y-0.5 rounded-md bg-gray-50 px-3 py-2">
      {Object.entries(obj).map(([k, v]) => (
        <div key={k} className="break-all text-[11px] text-gray-700">
          <span className="text-gray-500">{k}:</span> {v}
        </div>
      ))}
    </div>
  )
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Não foi possível copiar. Selecione o texto manualmente.')
    }
  }

  return (
    <button type="button" onClick={() => void copy()}
      className="inline-flex cursor-pointer items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-gray-600 ring-1 ring-gray-200 transition hover:bg-white">
      {copied ? <Check size={11} /> : <Copy size={11} />}{copied ? 'Copiado' : label}
    </button>
  )
}

/** Se a resposta for JSON, indenta; senão mostra crua (HTML de erro, texto). */
function formatarCorpo(body: string | null): string {
  if (!body) return '— vazio —'
  try {
    return JSON.stringify(JSON.parse(body), null, 2)
  } catch {
    return body
  }
}

/** Uma chamada em texto puro, para colar no e-mail/chamado da Medicar. */
function exchangeTexto(title: string, ex: MedicarExchange): string {
  const linhas = [
    `### ${title}`,
    `${ex.method} ${ex.url}`,
    ex.httpStatus !== null ? `HTTP ${ex.httpStatus}${ex.durationMs !== null ? ` (${ex.durationMs} ms)` : ''}` : '(sem resposta)',
    '',
    '-- Cabeçalhos enviados --',
    ...Object.entries(ex.requestHeaders ?? {}).map(([k, v]) => `${k}: ${v}`),
  ]
  if (ex.requestQuery && Object.keys(ex.requestQuery).length) {
    linhas.push('', '-- Query --', ...Object.entries(ex.requestQuery).map(([k, v]) => `${k}=${v}`))
  }
  if (ex.requestBody != null) {
    linhas.push('', '-- Corpo enviado --', JSON.stringify(ex.requestBody, null, 2))
  }
  if (Object.keys(ex.responseHeaders ?? {}).length) {
    linhas.push('', '-- Cabeçalhos da resposta --', ...Object.entries(ex.responseHeaders).map(([k, v]) => `${k}: ${v}`))
  }
  linhas.push('', '-- Corpo da resposta --', formatarCorpo(ex.responseBody))
  if (ex.error) linhas.push('', `-- Erro --`, ex.error)

  return linhas.join('\n')
}

/** As duas chamadas juntas, prontas para repassar. */
function diagnosticoTexto(auth: MedicarAuthTest): string {
  const partes = [
    'Diagnóstico da integração Medicar (a senha aparece mascarada)',
    `Gerado em ${new Date().toLocaleString('pt-BR')}`,
    '',
  ]
  if (auth.auth.exchange) partes.push(exchangeTexto('1 · Autenticação (token)', auth.auth.exchange), '')
  if (auth.contract?.exchange) partes.push(exchangeTexto('2 · Consulta de contrato', auth.contract.exchange), '')
  if (!auth.auth.exchange && !auth.contract?.exchange) {
    partes.push(`Sem detalhe HTTP. Erro: ${auth.auth.error ?? 'desconhecido'}`)
  }

  return partes.join('\n')
}

function Banner({ tone, icon, children }: { tone: 'rose' | 'amber'; icon: React.ReactNode; children: React.ReactNode }) {
  const cls = tone === 'rose' ? 'bg-rose-50 text-rose-900 ring-rose-200' : 'bg-amber-50 text-amber-900 ring-amber-200'
  return (
    <div className={`mb-4 flex items-start gap-2 rounded-md px-3 py-2.5 text-sm ring-1 ${cls}`}>
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span>{children}</span>
    </div>
  )
}

function JsonBlock({ title, json }: { title: string; json: string | null }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">{title}</p>
      <pre className="max-h-72 overflow-auto rounded-md bg-gray-900 px-3 py-2.5 text-xs leading-relaxed text-gray-100">
        {json ?? '— sem conteúdo —'}
      </pre>
    </div>
  )
}

export default IntegrationsMedicarPage
